import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Paper, TextField, Grid, IconButton,
    CircularProgress, Alert, Breadcrumbs, Link
} from '@mui/material';
import { ArrowBack, CloudUpload, Save, Delete } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createFeature, updateFeature } from '../../services/Api';

const CreateAndModifyFeatures = () => {
    const { id } = useParams();
    const location = useLocation();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        label: location.state?.feature?.label || '',
        featureImages: null
    });
    const [preview, setPreview] = useState(location.state?.feature?.icon || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    // If it's an edit but no state was passed, we warn the user or redirect
    useEffect(() => {
        if (isEdit && !location.state?.feature) {
            setError('Please navigate from the features list to edit.');
            // Optionally: setTimeout(() => navigate('/features'), 3000);
        }
    }, [isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, featureImages: file }));
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = {};
        if (!formData.label || !formData.label.trim()) errors.label = 'Feature Label is required';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors({});
        setLoading(true);
        setError(null);

        // const alphabetRegex = /^[a-zA-Z\s]+$/;
        // if (!alphabetRegex.test((formData.key || '').trim()) || !alphabetRegex.test((formData.label || '').trim())) {
        //     setError('Numeric and special characters are not allowed');
        //     setLoading(false);
        //     return;
        // }

        const data = new FormData();
        data.append('label', formData.label);
        if (formData.featureImages) {
            if (typeof formData.featureImages !== 'string' && !formData.featureImages.type.startsWith('image/')) {
                setError('Only images are allowed');
                setLoading(false);
                return;
            }
            data.append('featureImages', formData.featureImages);
        }

        try {
            let response;
            if (isEdit) {
                response = await updateFeature(id, data);
            } else {
                response = await createFeature(data);
            }

            if (response.success) {
                navigate('/features');
            } else {
                setError(response.message || 'Action failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/features'); }}>
                        Features
                    </Link>
                    <Typography color="text.primary">{isEdit ? 'Modify' : 'Create'}</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/features')} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4" sx={{ fontWeight: 300 }}>
                        {isEdit ? 'Modify Feature' : 'Create New Feature'}
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: 4, borderRadius: '15px', border: '1px solid', borderColor: 'divider' }}>
                <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Grid container spacing={3}>
                                {/* <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        label="Feature Key *"
                                        name="key"
                                        value={formData.key}
                                        onChange={handleChange}
                                        placeholder="e.g., isVeg, containsEgg"
                                        error={!!formErrors.key}
                                        helperText={formErrors.key || "The unique key used in internal logic"}
                                    />
                                </Grid> */}
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        label="Feature Label *"
                                        name="label"
                                        value={formData.label}
                                        onChange={handleChange}
                                        placeholder="e.g. Easy return/refund , Fast Delivery "
                                        error={!!formErrors.label}
                                        helperText={formErrors.label || "Display name for the feature"}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: '15px',
                                height: 250,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                position: 'relative'
                            }}>
                                {preview ? (
                                    <>
                                        <Box
                                            component="img"
                                            src={preview}
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        <IconButton
                                            sx={{
                                                position: 'absolute', top: 8, right: 8,
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                '&:hover': { backgroundColor: 'white' },
                                            }}
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, featureImages: null }));
                                                setPreview(null);
                                            }}
                                        >
                                            <Delete color="error" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">Upload Feature Image</Typography>
                                    </>
                                )}
                                <input
                                    id="file-input-feature"
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => {
                                        handleFileChange(e);
                                        e.target.value = null;
                                    }}
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                Note: Image size must be 100px * 100px
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 2, fontSize: "10px !important" }}
                                    startIcon={<CloudUpload />}
                                    onClick={() => document.getElementById("file-input-feature").click()}
                                >
                                    Upload Image
                                </Button>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                sx={{ borderRadius: '8px', px: 4 }}
                                onClick={() => navigate('/features')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                sx={{ borderRadius: '8px', px: 4 }}
                                disabled={loading}
                            >
                                {isEdit ? 'Update Feature' : 'Save Feature'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default CreateAndModifyFeatures;
